import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import StatusChip from '../../Shared/StatusChip';
import { Status, SurveyResponse, SurveyType } from '../../../shared/constants';
import {
  isSurveyAttentionResponse,
  surveyScoreToLabel,
} from '../../../shared/surveys';
import {
  faBarsFilter,
  faFaceMeh,
  faFaceFrown,
} from '@fortawesome/pro-solid-svg-icons';
import DashboardTableLegend from './DashboardTableLegend';
import CustomerCareDTable from '../../Shared/CustomerCareDTable';
import ContactActionCell from './ContactActionCell';
import NoDataCard from './NoDataCard';
import LoadingSpinner from '../../Shared/LoadingSpinner';
import { fetchHubOwners } from '../../../store/hubOwner/actionCreators';
import { parseDateToLocal } from '../../../shared/utils';
import { customError, orange, warningBg } from '../../../mainTheme';

interface PatientSurveyDetailPanelProps {
  selectedTab: number;
  isFacilityAdmin: boolean;
}

const PatientSurveyDetailPanel: React.FC<PatientSurveyDetailPanelProps> = ({
  selectedTab,
  isFacilityAdmin,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { hubOwners, isLoading } = useSelector(
    (state: RootState) => state.hubOwner,
  );

  useEffect(() => {
    if (!hubOwners?.length) {
      dispatch(fetchHubOwners());
    }
  }, [dispatch, hubOwners?.length]);

  const onViewAll = () => {
    navigate(`/v2/patient-survey`);
  };

  const columns = [
    {
      field: 'patient',
      headerName: 'Patient',
      flex: 1,
    },
    ...(selectedTab !== 2
      ? [
          {
            field: 'facility_name',
            headerName: isFacilityAdmin ? 'Facility location' : 'Facility',
            flex: 1,
          },
        ]
      : []),
    {
      field: 'survey_response',
      headerName: 'Survey response',
      flex: 1,
      sortComparator: (v1: any, v2: any) => {
        return v1.text.localeCompare(v2.text);
      },
      renderCell: (params: any) => (
        <Box display="flex" alignItems="center">
          <StatusChip
            icon={params.value.icon}
            iconColor={params.value.color}
            labelText={params.value.text}
            conditionalValue={
              params.value.text === Status.Unassigned ? true : false
            }
            backgroundColor={warningBg}
          />
        </Box>
      ),
    },
    { field: 'hub_serial_number', headerName: 'Hub serial number', flex: 1 },
    { field: 'last_seen_online', headerName: 'Last online', flex: 1 },
    {
      field: 'action',
      headerName: 'Actions',
      renderCell: (params: any) => (
        <ContactActionCell
          contactName={params.row.contact_name}
          contactPhone={params.row.contact_phone}
          contactEmail={params.row.contact_email}
        />
      ),
    },
  ];

  const rows: any[] = useMemo(() => {
    let filteredHubOwners = hubOwners || [];

    // The attention range shared with the location Insights tab
    // (SURVEY_ATTENTION_RESPONSES); the API may send the label or the score.
    filteredHubOwners = filteredHubOwners.filter((owner: any) =>
      isSurveyAttentionResponse(
        SurveyType.HubOverallExperience,
        owner.last_survey_response,
      ),
    );

    if (selectedTab === 1) {
      filteredHubOwners = filteredHubOwners.filter(
        (owner: any) => owner.facility_id != null,
      );
    } else if (selectedTab === 2) {
      filteredHubOwners = filteredHubOwners.filter(
        (owner: any) => owner.facility_id == null,
      );
    }

    return (
      filteredHubOwners?.map((owner: any) => {
        const hasPatient = owner?.first_name || owner?.last_name;
        let surveyResponseText: string;
        let surveyResponseColor: string;
        let surveyResponseIcon: IconDefinition;
        let backgroundColor = { warningBg };

        if (
          surveyScoreToLabel(
            SurveyType.HubOverallExperience,
            owner.last_survey_response,
          ) === SurveyResponse.VeryUnhappy
        ) {
          surveyResponseText = SurveyResponse.VeryUnhappy;
          surveyResponseColor = customError;
          surveyResponseIcon = faFaceFrown;
        } else {
          surveyResponseText = SurveyResponse.SomewhatUnhappy;
          surveyResponseColor = orange;
          surveyResponseIcon = faFaceMeh;
        }

        // Adapt action values based on Role
        const contactName = isFacilityAdmin
          ? `${owner?.first_name ?? ''} ${owner?.last_name ?? ''}`.trim()
          : owner.contact_name || null;
        const contactPhone = isFacilityAdmin
          ? owner.phone || null
          : owner.contact_phone || null;
        const contactEmail = isFacilityAdmin
          ? owner.email || null
          : owner.contact_email || null;

        return {
          id: owner.customer_id,
          patient: hasPatient
            ? `${owner?.first_name} ${owner?.last_name}`
            : 'N/A',
          facility_name: owner.facility_name || 'N/A',
          survey_response: {
            icon: surveyResponseIcon,
            color: surveyResponseColor,
            text: surveyResponseText,
            backgroundColor: backgroundColor,
          },
          hub_serial_number: owner?.serial_number || 'N/A',
          last_seen_online: owner.last_seen_online
            ? parseDateToLocal(owner.last_seen_online, 'yyyy-MM-dd HH:mm:ss')
            : 'N/A',
          action: faBarsFilter,
          contact_name: contactName,
          contact_phone: contactPhone,
          contact_email: contactEmail,
        };
      }) ?? []
    );
  }, [hubOwners, selectedTab, isFacilityAdmin]);

  const tableLegendData = [
    {
      name: SurveyResponse.VeryUnhappy,
      color: customError,
    },
    {
      name: SurveyResponse.SomewhatUnhappy,
      color: orange,
    },
  ];
  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {rows.length ? (
        <Box p={2}>
          <DashboardTableLegend tableLegendData={tableLegendData} />
          <CustomerCareDTable
            rows={rows}
            columns={columns}
            loading={isLoading}
            pageSize={5}
            onViewAll={onViewAll}
            defaultSortField="survey_response"
            defaultSortDirection="asc"
          />
        </Box>
      ) : (
        <NoDataCard />
      )}
    </>
  );
};

export default PatientSurveyDetailPanel;
